import { ChildAgePicker } from "@apac/hotels";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import { CoreHotelsComponent } from "@b2bportal/core-types";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTrigger,
  Icon,
} from "@b2bportal/core-ui";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  GuestCountPicker,
  type GuestPickerComponentProps,
} from "@tenant/hotels";
import clsx from "clsx";
import { MAX_HOTEL_GUESTS, useGuestPickerWithInfants } from "./hooks";
import styles from "./styles.module.scss";

export const GuestPicker = ({
  className,
  ...props
}: GuestPickerComponentProps) => {
  const { t } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, CoreHotelsComponent.GuestPicker);
  const { context, handlers, text } = useGuestPickerWithInfants(props);

  return (
    <div className={clsx(block, className)}>
      <Dialog
        onOpenChange={(open) => {
          if (!open) {
            handlers.resetGuestsToValidState();
          }
        }}
        modal={false}
      >
        <DialogTrigger asChild>
          <button
            className={cn("guest-button")}
            aria-label={t("passengerInformation.guests")}
          >
            {text.buttonLabel}
            <Icon iconName={IconNameEnum.guest} />
          </button>
        </DialogTrigger>
        <DialogContent
          showOverlay={true}
          title={t("passengerInformation.guests")}
          className={cn("guest-dialog")}
          bottomFixedContent={
            <DialogClose
              className={cn("apply-button")}
              disabled={!context.isGuestPickerValid}
              onClick={handlers.onSubmit}
            >
              {text.applyButtonLabel}
            </DialogClose>
          }
        >
          <div className={cn("dialog-content")}>
            <span className={cn("title")}>
              {t("home.passengerSelection.title")}
            </span>
            <span className={cn("description")}>
              {t("home.passengerSelection.hotelGuestLimit", {
                maxTravelers: MAX_HOTEL_GUESTS,
              })}
            </span>
            <GuestCountPicker
              title={text.adultTitle}
              subtitle={text.adultSubtitle}
              minimumCount={context.minimumAdultCount}
              count={context.adultCount}
              setCount={handlers.onAdultChange}
              maximumReached={context.maximumReached}
            />
            <GuestCountPicker
              title={text.childCountTitle}
              subtitle={text.childCountSubtitle}
              minimumCount={0}
              count={context.childrenCount}
              setCount={handlers.setChildrenCount}
              maximumReached={context.maximumReached}
            />

            {context.childrenCount > 0 && (
              <div className={cn("age-picker")}>
                <div className={cn("age-picker-title")}>
                  {t("core-hotels.search.guestPicker.child.selectPlaceHolder")}
                </div>
                <div className={cn("age-picker-subtitle")}>
                  {t("guestCount.childrenAgeMessage")}
                </div>

                <div className={cn("age-picker-input")}>
                  {Array.from({ length: context.childrenCount }).map(
                    (_, index) => {
                      return (
                        <ChildAgePicker
                          className={cn("child-age-picker")}
                          key={index}
                          id={index + 1}
                          onAgeSelectHandler={(age: number) => {
                            handlers.updateChildAge(index, age);
                          }}
                          childAge={context.childAgeArr[index]}
                          label={text.childAgeLabel(index)}
                        />
                      );
                    }
                  )}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
